import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useChargingSessionsV1 } from 'src/Libs/e-mobility/charging-sessions'
import { Stack, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import ChargingSessionsOverviewDialog from '../../ChargingSessionsOverviewDialog'
import ChargingSessionsOverviewSkeleton from '../../ChargingSessionsOverviewSkeleton'
import ChargingSessionChartContainer from '../../emobility-charging-sessions/ChargingSessionChartContainer'
import ChargingSessionTable from '../../emobility-charging-sessions/ChargingSessionTable'
import { ChargeDurationCard } from '../../emobility-charging-sessions/stat-cards/ChargeDurationCard'
import { SolarEnergyCard } from '../../emobility-charging-sessions/stat-cards/SolarEnergyCard'
import { TotalEnergyCard } from '../../emobility-charging-sessions/stat-cards/TotalEnergyCard'

export interface EMobilityChargingSessionsOverviewModalProps {
  open: boolean
  onClose: () => void
  place: number
  rfid: string
  storageId: string
  chargingCard: string
}

const useStyles = makeStyles()(theme => ({
  cardStats: {
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
}))

const EMobilityChargingSessionsOverviewModal: React.FC<EMobilityChargingSessionsOverviewModalProps> = ({
  open,
  onClose,
  place,
  rfid,
  storageId,
  chargingCard,
}): JSX.Element => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const kwhUnit = 1000
  const { chargingSessions, wallboxes, isLoading: isChargingSessionsLoading } = useChargingSessionsV1(storageId)
  const sessions = chargingSessions ? chargingSessions.filter(item => item.authentication.rfid === rfid && item.authentication.type === 'RFID') : []
  const totalChargeTime = sessions.reduce((a, b) => a + b.chargeTimeActive, 0)
  const totalEnergyKWh = sessions.reduce((a, b) => a + b.energyAll, 0) / kwhUnit
  const totalSolarEnergyKWh = sessions.reduce((a, b) => a + b.energySolar, 0) / kwhUnit
  const chargingSessionsLoading = isChargingSessionsLoading || !wallboxes

  return (
    <ChargingSessionsOverviewDialog
      open={open}
      place={place}
      title={t('emobility.chargingCards.sessionsDialogTitle', { chargingCardName: chargingCard, rfid: rfid })}
      onClose={onClose}
    >
      {chargingSessionsLoading && <ChargingSessionsOverviewSkeleton />}
      {!chargingSessionsLoading && (
        <Box>
          <Stack direction="row" rowGap={2} columnGap={2} className={classes.cardStats} flexWrap="wrap">
            <ChargeDurationCard chargeDurationMilliseconds={totalChargeTime} />
            <TotalEnergyCard energyKWh={totalEnergyKWh} />
            <SolarEnergyCard energyKWh={totalSolarEnergyKWh} />
          </Stack>

          <Box className={classes.tableContainer}>
            <ChargingSessionTable chargingSessions={sessions} wallboxes={wallboxes} />
          </Box>

          <ChargingSessionChartContainer chargingSessions={sessions} />
        </Box>
      )}
    </ChargingSessionsOverviewDialog>
  )
}

export default EMobilityChargingSessionsOverviewModal
